import React from 'react';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowLeft, TrendingUp, Calendar, Clock } from 'lucide-react';

const ArticleQualityMetrics = () => {
  const siteUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || 'https://orboroi.com';
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Resources", url: "/resources" },
    { name: "Data Quality Metrics", url: "/resources/quality-metrics" }
  ];
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Essential Data Quality Metrics for AI Success",
    "description": "Track and improve AI data quality with key metrics: accuracy, consistency, completeness, and inter-annotator agreement for production systems.",
    "datePublished": "2024-11-28",
    "author": { "@type": "Organization", "name": "Orboroi" },
    "publisher": { "@type": "Organization", "name": "Orboroi", "logo": { "@type": "ImageObject", "url": `${siteUrl}/logo.png` } },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${siteUrl}/resources/quality-metrics` }
  };
  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <SEO title="Essential Data Quality Metrics for AI Success" description="Track and improve AI data quality with key metrics: accuracy, consistency, completeness, and inter-annotator agreement." canonical="/resources/quality-metrics" type="article" author="Orboroi Team" datePublished="2024-11-28" structuredData={structuredData} breadcrumbs={breadcrumbs} />
      <div className="container mx-auto px-6 py-6">
        <Link to="/resources">
          <Button variant="ghost" className="text-[#379F7F] hover:bg-[#379F7F]/10">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Resources
          </Button>
        </Link>
      </div>

      <section className="py-8 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8 text-[#379F7F]" />
              <span className="text-[#379F7F] font-semibold">Model Evaluation</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Quality Metrics That Matter for Production AI
            </h1>
            
            <div className="flex items-center gap-6 text-gray-400 mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Nov 8, 2024</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>8 min read</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <article className="prose prose-invert prose-lg max-w-none">
              
              <div className="mb-12">
                <p className="text-xl text-gray-300 leading-relaxed mb-6">
                  Accuracy alone doesn't tell you if your AI model is production-ready. A model with 95% accuracy might 
                  still fail catastrophically in production if it's confident about wrong predictions, biased against 
                  certain groups, or slow to respond in time-critical situations.
                </p>
                <p className="text-lg text-gray-400 leading-relaxed">
                  This article explores the quality metrics that truly matter for production AI systems and how to track 
                  them effectively.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Beyond Accuracy: Production-Critical Metrics</h2>
                
                <div className="space-y-6">
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                    <h3 className="text-xl font-bold text-[#379F7F] mb-3">Precision and Recall</h3>
                    <p className="text-gray-300 mb-3">
                      Different applications require different trade-offs:
                    </p>
                    <ul className="space-y-2">
                      <li className="text-gray-300">
                        <strong className="text-white">High Precision:</strong> Critical when false positives are costly 
                        (fraud detection, spam filtering)
                      </li>
                      <li className="text-gray-300">
                        <strong className="text-white">High Recall:</strong> Essential when missing positives is dangerous 
                        (medical diagnosis, security threats)
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                    <h3 className="text-xl font-bold text-[#379F7F] mb-3">Confidence Calibration</h3>
                    <p className="text-gray-300">
                      A well-calibrated model's confidence scores reflect true probabilities. If your model says 80% 
                      confidence, it should be correct ~80% of the time. Poor calibration makes models unreliable for 
                      decision-making.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Data Quality Metrics</h2>
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  Model performance is only as good as training data quality:
                </p>
                
                <ul className="space-y-3 mb-6">
                  <li className="text-lg text-gray-300 flex items-start">
                    <span className="text-[#379F7F] mr-3 mt-1">•</span>
                    <span><strong className="text-white">Label Consistency:</strong> Inter-annotator agreement scores 
                    (target: 90%+ for production)</span>
                  </li>
                  <li className="text-lg text-gray-300 flex items-start">
                    <span className="text-[#379F7F] mr-3 mt-1">•</span>
                    <span><strong className="text-white">Coverage Metrics:</strong> Distribution of examples across all 
                    classes and scenarios</span>
                  </li>
                  <li className="text-lg text-gray-300 flex items-start">
                    <span className="text-[#379F7F] mr-3 mt-1">•</span>
                    <span><strong className="text-white">Error Rate:</strong> Percentage of incorrect labels in training data</span>
                  </li>
                </ul>
              </div>

              <div className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Operational Metrics</h2>
                
                <div className="border-l-4 border-[#379F7F] pl-6 py-2 mb-6">
                  <h4 className="text-xl font-bold text-white mb-2">Latency and Throughput</h4>
                  <p className="text-gray-300">
                    Real-time applications have strict latency requirements. Track P50, P95, and P99 latencies to ensure 
                    consistent performance.
                  </p>
                </div>
                
                <div className="border-l-4 border-[#379F7F] pl-6 py-2 mb-6">
                  <h4 className="text-xl font-bold text-white mb-2">Fairness Metrics</h4>
                  <p className="text-gray-300">
                    Measure performance across demographic groups to identify and mitigate bias. Equal opportunity and 
                    demographic parity are common fairness metrics.
                  </p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Monitoring in Production</h2>
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  Continuous monitoring ensures models remain reliable over time:
                </p>
                
                <div className="bg-gray-800 border border-[#379F7F]/30 rounded-lg p-8 mb-6">
                  <h3 className="text-2xl font-bold text-[#379F7F] mb-4">Key Monitoring Metrics:</h3>
                  <ul className="space-y-3">
                    <li className="text-gray-300">
                      <strong className="text-white">Prediction Distribution:</strong> Track changes in output distribution
                    </li>
                    <li className="text-gray-300">
                      <strong className="text-white">Input Distribution Shift:</strong> Monitor for data drift
                    </li>
                    <li className="text-gray-300">
                      <strong className="text-white">User Feedback:</strong> Corrections, rejections, and satisfaction scores
                    </li>
                    <li className="text-gray-300">
                      <strong className="text-white">Error Clustering:</strong> Identify systematic failure patterns
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Conclusion</h2>
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  Production-ready AI requires comprehensive quality metrics that go far beyond test set accuracy. By 
                  tracking the right metrics—precision/recall, calibration, data quality, fairness, and operational 
                  performance—organizations can ensure their AI systems deliver reliable value in production.
                </p>
                <p className="text-lg text-gray-300 leading-relaxed">
                  The key is establishing these metrics early and maintaining them throughout the model lifecycle.
                </p>
              </div>

              <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-8 mt-12">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Comprehensive Quality Assurance
                </h3>
                <p className="text-gray-300 mb-6">
                  Orboroi provides end-to-end quality metrics tracking and monitoring for production AI systems.
                </p>
                <Link to="/contact">
                  <Button className="bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold">
                    Talk to a Data Expert
                  </Button>
                </Link>
              </div>

            </article>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticleQualityMetrics;
